import { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Tooltip,
  Input,
  Button,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaFileExcel } from "react-icons/fa";
import CustomDatePicker from "../CustomDatePicker";
import { Corban } from "@/models/corban.model";
import { downloadConciliationExcel } from "@/services/financial-service/conciliation.service";
import { getCorbans } from "@/services/corban.service";
import DebouncedAutocompleteInput, { AutocompleteOption } from "../FinancialShared/DebouncedAutocompleteInput";
import {
  searchPromotoras,
  searchCorbans,
  searchGerentes,
  searchVendedores,
} from "@/services/financial-service/global-state.service";

interface CommissionsTabProps {
  backgroundStyle: any;
}

export default function ConciliationTab({
  backgroundStyle,
}: CommissionsTabProps) {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [selectedCorban, setSelectedCorban] = useState<AutocompleteOption | null>(null);
  const [selectedPromotora, setSelectedPromotora] = useState<AutocompleteOption | null>(null);
  const [selectedGerente, setSelectedGerente] = useState<AutocompleteOption | null>(null);
  const [selectedVendedor, setSelectedVendedor] = useState<AutocompleteOption | null>(null);

  const [filterPromotoraName, setFilterPromotoraName] = useState<string>("");
  const [filterGerenteName, setFilterGerenteName] = useState<string>("");
  const [filterId, setFilterId] = useState<string>("");
  const [filterLegalName, setFilterLegalName] = useState<string>("");
  const [filterDocument, setFilterDocument] = useState<string>("");
  const [filterBusinessName, setFilterBusinessName] = useState<string>("");
  const [filterVendedorInput, setFilterVendedorInput] = useState<string>("");
  const [corbans, setCorbans] = useState<Corban[]>([]);

  const toast = useToast();

  useEffect(() => {
    const fetchCorbans = async () => {
      try {
        const response = await getCorbans();
        setCorbans(response);
      } catch (error) {
        console.error("Erro ao obter a lista de Corbans:", error);
        toast({
          title: "Erro ao obter a lista de Corbans.",
          description: "Tente novamente mais tarde.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchCorbans();
  }, [toast]);

  const filteredCorbans = corbans.filter((corban) => {
    const matchesId =
      filterId.trim() === "" ||
      String(corban.id).toLowerCase().includes(filterId.toLowerCase());

    const matchesLegalName =
      filterLegalName.trim() === "" ||
      corban.legalName?.toLowerCase().includes(filterLegalName.toLowerCase());

    const matchesDocument =
      filterDocument.trim() === "" ||
      corban.document?.toLowerCase().includes(filterDocument.toLowerCase());

    const matchesBusinessName =
      selectedCorban === null ||
      corban.id === Number(selectedCorban.value);

    // Note: Local filtering for Promotora and Gerente might be limited if the data is not in the object,
    // but the user asked for these filters. We'll use the IDs if available.
    const matchesPromotora =
      selectedPromotora === null ||
      corban.creditPromoterId === Number(selectedPromotora.value);

    const matchesGerente =
      selectedGerente === null ||
      corban.FinancialSettings?.commercialManagerId === selectedGerente.value;

    const matchesVendedor =
      selectedVendedor === null ||
      (selectedVendedor.value &&
        corban.sellerAuthorizedDocument?.toLowerCase().includes(String(selectedVendedor.value).toLowerCase()));

    return (
      matchesId &&
      matchesLegalName &&
      matchesDocument &&
      matchesBusinessName &&
      matchesPromotora &&
      matchesGerente &&
      matchesVendedor
    );
  });

  const handleDownload = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      toast({
        title: "Selecione o intervalo de datas para consulta",
        description: "Por favor, selecione um intervalo de datas para consulta",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const isAnyFilterApplied =
      filterId.trim() !== "" ||
      filterLegalName.trim() !== "" ||
      filterDocument.trim() !== "" ||
      selectedCorban !== null ||
      selectedPromotora !== null ||
      selectedGerente !== null ||
      selectedVendedor !== null;

    const corbanIds = isAnyFilterApplied
      ? filteredCorbans.map((o) => o.id!)
      : [];

    try {
      const blob = await downloadConciliationExcel(
        dateRange[0],
        dateRange[1],
        corbanIds
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "conciliado.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Sucesso",
        description: "O relatório foi gerado e baixado com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao baixar o relatório:", error);
      toast({
        title: "Erro",
        description:
          "Ocorreu um erro ao gerar o relatório. Por favor, tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box width="100%">
      <Box
        {...backgroundStyle}
        borderRadius="xl"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="xl"
        width="100%"
        mb={4}
        h="auto"
      >
        <Text fontSize="xl" fontWeight="bold" fontFamily="Lexend Deca" mb={4}>
          FILTROS DE CONCILIAÇÃO
        </Text>
        <Flex flexDirection={"column"} gap={4}>
          <Flex gap={4}>
            <CustomDatePicker
              dateRange={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
            <Box
              width={{ base: "100%", md: "70%", lg: "20%" }}
              mt={{ base: 5, md: 0 }}
              flex={1}
            >
              <DebouncedAutocompleteInput
                placeholder="Filtrar por Promotora"
                tooltip="Filtro de Promotoras por Nome"
                inputValue={filterPromotoraName}
                onInputChange={(val) => {
                  setFilterPromotoraName(val);
                  setSelectedPromotora(null);
                }}
                onSelect={(label, value) => {
                  setFilterPromotoraName(label);
                  setSelectedPromotora({ label, value });
                }}
                onClear={() => {
                  setFilterPromotoraName("");
                  setSelectedPromotora(null);
                }}
                fetchOptions={searchPromotoras}
              />
            </Box>

            <Box
              width={{ base: "100%", md: "70%", lg: "20%" }}
              mt={{ base: 5, md: 0 }}
              flex={1}
            >
              <DebouncedAutocompleteInput
                placeholder="Filtrar por Corban"
                tooltip="Filtro de Corbans por Nome Fantasia"
                inputValue={filterBusinessName}
                onInputChange={(val) => {
                  setFilterBusinessName(val);
                  setSelectedCorban(null);
                }}
                onSelect={(label, value) => {
                  setFilterBusinessName(label);
                  setSelectedCorban({ label, value });
                }}
                onClear={() => {
                  setFilterBusinessName("");
                  setSelectedCorban(null);
                }}
                fetchOptions={searchCorbans}
              />
            </Box>

            <Box
              width={{ base: "100%", md: "70%", lg: "20%" }}
              mt={{ base: 5, md: 0 }}
              flex={1}
            >
              <DebouncedAutocompleteInput
                placeholder="Filtrar por Gerente"
                tooltip="Filtro de Gerentes Comerciais por Nome"
                inputValue={filterGerenteName}
                onInputChange={(val) => {
                  setFilterGerenteName(val);
                  setSelectedGerente(null);
                }}
                onSelect={(label, value) => {
                  setFilterGerenteName(label);
                  setSelectedGerente({ label, value });
                }}
                onClear={() => {
                  setFilterGerenteName("");
                  setSelectedGerente(null);
                }}
                fetchOptions={searchGerentes}
              />
            </Box>

            <Box
              width={{ base: "100%", md: "70%", lg: "20%" }}
              mt={{ base: 5, md: 0 }}
              flex={1}
            >
              <DebouncedAutocompleteInput
                placeholder="Filtrar por Vendedor"
                tooltip="Filtrar por nome do Vendedor"
                inputValue={filterVendedorInput}
                onInputChange={(val) => {
                  setFilterVendedorInput(val);
                  setSelectedVendedor(null);
                }}
                onSelect={(label, value) => {
                  setFilterVendedorInput(label);
                  setSelectedVendedor({ label, value });
                }}
                onClear={() => {
                  setFilterVendedorInput("");
                  setSelectedVendedor(null);
                }}
                fetchOptions={searchVendedores}
              />
            </Box>

            <Flex textAlign={"center"}>
              <Button
                color={"white"}
                gap={"2"}
                colorScheme="green"
                textTransform={"uppercase"}
                onClick={handleDownload}
                height="3rem"
              >
                gerar relatório
                <FaFileExcel />
              </Button>
            </Flex>
          </Flex>

          <Flex gap={4} flexDirection={{ base: "column", md: "row" }}>
            <Box flex={1}>
              <Tooltip label="Filtrar por ID do Corban" fontSize="md" placement="bottom">
                <Input
                  placeholder="Filtrar por ID"
                  bg="white"
                  height="3rem"
                  color="black"
                  fontFamily="Lexend Deca"
                  fontSize="1rem"
                  value={filterId}
                  onChange={(e) => setFilterId(e.target.value)}
                />
              </Tooltip>
            </Box>

            <Box flex={1}>
              <Tooltip label="Filtrar por Razão Social" fontSize="md" placement="bottom">
                <Input
                  placeholder="Filtrar por Razão Social"
                  bg="white"
                  height="3rem"
                  color="black"
                  fontFamily="Lexend Deca"
                  fontSize="1rem"
                  value={filterLegalName}
                  onChange={(e) => setFilterLegalName(e.target.value)}
                />
              </Tooltip>
            </Box>

            <Box flex={1}>
              <Tooltip label="Filtrar por CNPJ" fontSize="md" placement="bottom">
                <Input
                  placeholder="Filtrar por CNPJ"
                  bg="white"
                  height="3rem"
                  color="black"
                  fontFamily="Lexend Deca"
                  fontSize="1rem"
                  value={filterDocument}
                  onChange={(e) => setFilterDocument(e.target.value)}
                />
              </Tooltip>
            </Box>
          </Flex>
        </Flex>
      </Box>

      <Box
        w={"100%"}
        p={{ base: 6, md: 6 }}
        borderWidth="1px"
        borderRadius="md"
        boxShadow="base"
        bg="white"
        h={"auto"}
      >
        <Text fontWeight="bold" mt={4} mb={2}>
          Resultados do Filtro:
        </Text>
        {filteredCorbans.map((corban) => (
          <Flex
            key={corban.id}
            p={2}
            borderWidth="1px"
            borderRadius="md"
            bg="gray.50"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box>
              <Text>ID: {corban.id}</Text>
              <Text>Razão Social: {corban.legalName}</Text>
              <Text>CNPJ: {corban.document}</Text>
              <Text>Nome Fantasia: {corban.businessName}</Text>
            </Box>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}
